// SPDX-License-Identifier: AGPL-3.0-or-later
// KONVO: whether Agents is switched on for the signed-in person (the per-person rollout switch
// "agents", managed in Team insights; staff always have it). When it is off, the Agents page,
// the Connections card and the notification settings stay hidden.
//
// Same rhythm as EngagementAccess: loaded on start, when the window returns (at most once a
// minute) and every 10 visible minutes; the last answer per person is remembered on the device so
// the sidebar can show the page from the first frame.

import {Endpoints} from '@app/features/app/constants/Endpoints';
import {getProtectedLocalStorage} from '@app/features/platform/state/ProtectedWebStorage';
import {http} from '@app/features/platform/transport/RestTransport';
import {VisiblePoll} from '@app/features/ui/utils/VisiblePoll';
import Window from '@app/features/window/state/Window';
import {makeAutoObservable, reaction, runInAction} from 'mobx';

export const AGENTS_ACCESS_REFRESH_MS = 10 * 60 * 1000;
export const AGENTS_ACCESS_FOCUS_GAP_MS = 60 * 1000;
const STORAGE_PREFIX = 'konvo:agents-access:';

function remembered(userId: string): boolean | null {
	try {
		const raw = getProtectedLocalStorage()?.getItem(STORAGE_PREFIX + userId);
		return raw === '1' ? true : raw === '0' ? false : null;
	} catch {
		return null;
	}
}

function remember(userId: string, enabled: boolean): void {
	try {
		getProtectedLocalStorage()?.setItem(STORAGE_PREFIX + userId, enabled ? '1' : '0');
	} catch {
		// Private mode or full storage: the next start asks the server again.
	}
}

export class AgentsAccessStore {
	/** Agents is on for the signed-in person (off until known). */
	enabled = false;
	/** The server answered at least once for this person. */
	known = false;
	private userId: string | null = null;
	private generation = 0;
	private stops: Array<() => void> = [];
	private loadedAt = Number.NEGATIVE_INFINITY;
	private pending: Promise<void> | null = null;

	constructor() {
		makeAutoObservable<this, 'userId' | 'generation' | 'stops' | 'loadedAt' | 'pending'>(
			this,
			{userId: false, generation: false, stops: false, loadedAt: false, pending: false},
			{autoBind: true},
		);
	}

	/** Start for this person (mounted once signed in); the returned function stops and forgets. */
	start(userId: string): () => void {
		this.reset();
		this.userId = userId;
		const cached = remembered(userId);
		this.enabled = cached ?? false;
		const onFocus = () => {
			if (Date.now() - this.loadedAt >= AGENTS_ACCESS_FOCUS_GAP_MS) void this.load();
		};
		window.addEventListener('focus', onFocus);
		const poll = new VisiblePoll(
			AGENTS_ACCESS_REFRESH_MS,
			() => {
				if (Date.now() - this.loadedAt >= AGENTS_ACCESS_FOCUS_GAP_MS) return this.load();
				return undefined;
			},
			false,
			AGENTS_ACCESS_REFRESH_MS,
		);
		poll.start();
		const stopVisibility = reaction(
			() => Window.visible,
			(visible) => {
				if (visible) onFocus();
			},
		);
		this.stops = [() => window.removeEventListener('focus', onFocus), () => poll.stop(), stopVisibility];
		void this.load();
		return () => this.reset();
	}

	/** Ask the server; a failure keeps what was known. */
	load(): Promise<void> {
		const userId = this.userId;
		if (!userId || !Window.visible) return Promise.resolve();
		if (this.pending) return this.pending;
		const request = this.fetch(userId, this.generation).finally(() => {
			if (this.pending === request) this.pending = null;
		});
		this.pending = request;
		return request;
	}

	private async fetch(userId: string, generation: number): Promise<void> {
		try {
			const response = await http.get<{enabled?: unknown}>(Endpoints.KONVO_AGENTS_ACCESS, {mode: 'silent'});
			if (generation !== this.generation || typeof response.body?.enabled !== 'boolean') return;
			const enabled = response.body.enabled;
			runInAction(() => {
				this.enabled = enabled;
				this.known = true;
				this.loadedAt = Date.now();
			});
			remember(userId, enabled);
		} catch {
			// Offline or a blip: keep the last answer until the next focus or interval.
		}
	}

	/** The overview said the switch is off (or on): follow it without waiting for the next poll. */
	note(enabled: boolean): void {
		this.enabled = enabled;
		this.known = true;
		if (this.userId) remember(this.userId, enabled);
	}

	/** Sign-out or account switch: stop and forget. */
	reset(): void {
		this.generation += 1;
		for (const stop of this.stops) stop();
		this.stops = [];
		this.userId = null;
		this.enabled = false;
		this.known = false;
		this.loadedAt = Number.NEGATIVE_INFINITY;
		this.pending = null;
	}
}

export default new AgentsAccessStore();
